<template>
  <table v-if="activeSubscriptions.length">
    <thead>
      <tr>
        <th />
        <th
          v-for="subscription in subscriptions"
          :key="subscription.productCode"
          :class="[
            $style.subscriptionHeader,
            { [$style.highlightCellTop]: subscription.isBetterValueSub }
          ]"
          :data-test-id="`FeaturePricingMatrix__subscription__${subscription.productCode}`"
        >
          {{ subscription.displayName }}
          <div :class="$style.subDescription">
            {{ subscription.description }}
          </div>
        </th>
      </tr>
    </thead>
    <tbody
      v-for="(featureGroup) in featureGroups"
      :key="featureGroup.name"
      :data-test-id="`FeaturePricingMatrix__featureGroup__${featureGroup.name}`"
    >
      <tr>
        <td :class="[$style.bold, $style.featureGroupName]">
          {{ featureGroup.name }}
        </td>
        <td
          v-for="subscription in subscriptions"
          :key="subscription.productCode"
          :class="{ [$style.highlightCellSides]: subscription.isBetterValueSub }"
        />
      </tr>
      <tr
        v-for="(feature, index) in featureGroup.features"
        :key="index"
        :class="{ [$style.attributeRowDescription]: isFeatureShown(feature) }"
      >
        <th
          v-if="feature.attribute === $options.Attributes.cloudDirectoryAndSupport"
          :class="$style.attribute"
          data-test-id="Attribute__cloudDirectoryAndSupport"
        >
          <Tooltip
            :text="getCloudDirectoryDescription(feature.attribute)"
          >
            <span :class="$style.attributeWithDescription">
              {{ getCloudDirectoryName(feature.attribute) }}
            </span>
          </Tooltip>
          &
          <Tooltip
            :text="getStandardSupportDescription(feature.attribute)"
          >
            <span :class="$style.attributeWithDescription">
              {{ getStandardSupportName(feature.attribute) }}
            </span>
          </Tooltip>
        </th>
        <th
          v-else-if="isFeatureShown(feature)"
          :class="[
            $style.attribute,
            { [$style.attributeWithIcon]: getRequirementsTooltipText(feature) },
          ]"
          :data-test-id="`FeaturePricingMatrix__feature__${feature.attribute}`"
        >
          <Tooltip
            v-if="getAttributeDescription(feature.attribute)"
            :text="getAttributeDescription(feature.attribute)"
          >
            <span :class="$style.attributeWithDescription">
              {{ getAttributeDisplayName(feature.attribute) }}
            </span>
          </Tooltip>
          <span v-else>
            {{ getAttributeDisplayName(feature.attribute) }}
          </span>
          <TooltipIcon
            v-if="getRequirementsTooltipText(feature)"
            :data-test-id="`FeaturePricingMatrix__tooltipIcon__${feature.attribute}`"
            icon="info"
            :text="getRequirementsTooltipText(feature)"
          />
        </th>
        <td
          v-for="subscription in subscriptions"
          :key="subscription.productCode"
          :class="[
            $style.cellContent,
            { [$style.highlightCellSides]: subscription.isBetterValueSub }
          ]"
        >
          <Icon
            v-if="isCheckmarked(subscription.features, feature.name)"
            :class="$style.featureIcon"
            icon="checkmarkCircle"
            :scale="1"
          />
          <div
            v-else-if="shouldShowFeaturePricing(subscription, feature) && isFeatureShown(feature)"
            :class="$style.attributeDetailsContainer"
          >
            <Checkbox
              v-if="isALaCarteCheckbox(subscription, feature)"
              :class="$style.attributeCheckbox"
              :data-test-id="`Checkbox__aLaCarte__${feature.attribute}`"
              :isDisabled="isCheckboxDisabled(subscription.productCode, feature)"
              :value="isCheckboxActive(feature)"
              @input="handleCheckboxClicked(subscription, feature)"
            />
            {{ individualFeaturePrice(subscription, feature) }}
          </div>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <th :class="$style.annualPricing" />
        <td
          v-for="subscription in subscriptions"
          :key="subscription.productCode"
          :class="[
            $style.annualPricing,
            $style.subscriptionFooter,
            { [$style.highlightCellBot]: subscription.isBetterValueSub }
          ]"
          :data-test-id="`FeaturePricingMatrix__subscriptionPrice__${subscription.productCode}`"
        >
          <span :class="[$style.bold, $style.footerSubscriptionPrice]">
            ${{ price(subscription) }}
          </span>
          <span>/user/mo</span>
          <div
            v-if="!isALaCarte(subscription)"
            :class="$style.footerButtonContainer"
          >
            <Button
              :data-test-id="`FeaturePricingMatrix__cta__${subscription.productCode}`"
              :outline="true"
              text="buy now"
              type="secondary"
              @click="openPaymentForm(subscription)"
            />
          </div>
          <div
            v-else
            :class="$style.footerButtonContainer"
          >
            <Button
              :data-test-id="`FeaturePricingMatrix__cta__${subscription.productCode}`"
              :outline="true"
              text="select features"
              type="secondary"
              @click="handleSelectFeaturesClick(subscription)"
            />
          </div>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import {
  Button,
  Checkbox,
  Icon,
  Tooltip,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PaymentHelper from 'client/Payments/PaymentHelper';
import PaymentStepMixin from 'client/Payments/mixins/PaymentStepMixin';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import SubscriptionFeaturesMixin from 'client/Payments/pricingMatrix/SubscriptionFeaturesMixin';
import Util from 'client/util/Util';

const aLaCarteComponentCollection = 'ALaCarteComponentCollection';
const subscriptionCollection = 'SubscriptionCollection';
const checkoutModel = 'CheckoutModel';
const betterValuePackageModule = 'BetterValuePackageModule';

const {
  getProductCodeByType,
  Attributes,
  FeatureGroups,
  ProductCodeToSubscriptionType,
  SubscriptionDetails,
  SubscriptionTypes,
} = SubscriptionConstants;

const {
  description,
  location,
  text,
  section,
} = eventProperties;
const {
  pricingPage,
} = eventLocations;

export default {
  name: 'FeaturePricingMatrix',

  components: {
    Button,
    Checkbox,
    Icon,
    Tooltip,
    TooltipIcon,
  },

  mixins: [PaymentStepMixin, SubscriptionFeaturesMixin],

  Attributes,
  EventHelper,
  ModalEventBus,
  PaymentHelper,
  Util,

  props: {
    isAnnual: Boolean,
  },

  data() {
    const {
      cloudDirectory,
      coreCloudDirectory,
      webSSOCustom,
      ...matrixFeatureGroups
    } = FeatureGroups;
    return {
      showAddOns: false,
      featureGroups: {
        ...matrixFeatureGroups,
      },
    };
  },

  computed: {
    ...mapGetters(subscriptionCollection, {
      activeSubscriptions: 'getCollection',
      isDefaultAddOn: 'getIsDefaultComponent',
      subscriptionPrice: 'getSubscriptionPrice',
    }),

    ...mapGetters(aLaCarteComponentCollection, {
      aLaCarteComponents: 'getCollection',
    }),

    ...mapGetters(betterValuePackageModule, {
      alaCarteBetterValueSub: 'betterValuePackage',
    }),

    ...mapGetters(checkoutModel, {
      addOns: 'getAddOnProductCodes',
      cloudDirectoryAndSupport: 'cloudDirectoryAndSupport',
      nonDefaultAddons: 'getSelectedAddOns',
    }),

    billingType() {
      return this.isAnnual ? 'Annual Billing' : 'Monthly Billing';
    },

    subscriptions() {
      const subscriptions = [
        ...this.activeSubscriptions,
      ];

      return subscriptions.map((subscription) => {
        const subscriptionType = ProductCodeToSubscriptionType[subscription.productCode];
        const betterValueProductCode = this.alaCarteBetterValueSub ? this.alaCarteBetterValueSub.productCode : '';
        return {
          type: subscriptionType,
          isBetterValueSub: betterValueProductCode === subscription.productCode,
          ...SubscriptionDetails[subscriptionType],
          ...subscription,
        };
      });
    },
  },

  mounted() {
    this.showAddOns = this.$hasFeatureFlag(this.$featureFlags.purchaseAddOnsInProduct);
    this.fetchALaCarteComponents();
  },

  destroyed() {
    this.resetSubscription();
  },

  methods: {
    ...mapActions(aLaCarteComponentCollection, {
      fetchALaCarteComponents: 'fetchCollection',
    }),

    ...mapActions(checkoutModel, [
      'toggleAddOn',
    ]),

    getALaCarteComponentFromFeature(feature) {
      return this.aLaCarteComponents
        .find(component => component.productCode === this.getFeatureProductCode(feature));
    },

    getAddOnFromFeature(feature) {
      const component = this.getALaCarteComponentFromFeature(feature);

      return {
        ...component,
        displayName: this.getAttributeDisplayName(feature.attribute),
        icon: this.getAttributeIcon(feature.attribute),
      };
    },

    getFeatureProductCode(feature) {
      const addOn = this.aLaCarteComponents
        .find((component) => component.features[0].name === feature.name);
      return addOn ? addOn.productCode : '';
    },

    getRequirementsTooltipText(feature) {
      const component = this.getALaCarteComponentFromFeature(feature);
      let tooltipText = '';

      if (component?.requiresAtLeastOneAddOn) {
        tooltipText = `Select an a la carte feature first to add ${component.displayName} to your cart`;
      } else if (component?.requires?.length > 0) {
        const requirementsList = component.requires
          .map(requiredProductCode => this.aLaCarteComponents
            .find(comp => comp.productCode === requiredProductCode)?.displayName)
          .filter(name => !!name)
          .join(' or ');
        const requirementsText = `${component.requires.length > 1 ? 'either ' : ''}${requirementsList}`;
        tooltipText = `Select ${requirementsText} first to add ${component.displayName} to your cart`;
      }

      return tooltipText;
    },

    async handleCheckboxClicked(sub, feature) {
      // Case: first checkbox clicked
      if (this.subscription.addOns.length === 0) {
        this.updateItem(sub);
        this.toggleAddOn(this.cloudDirectoryAndSupport);
      }

      // Toggle the checkbox in the store
      const addOn = this.getAddOnFromFeature(feature);
      this.toggleAddOn(addOn);

      const checkboxState = this.isCheckboxActive(feature);
      UiEvents.triggerCheckboxClicked({
        value: checkboxState,
        description: `A La Carte checkbox set to ${checkboxState} for ${addOn.displayName}`,
        page: this.$page,
        section: 'Feature Pricing Matrix',
      });
    },

    handleSelectFeaturesClick(subscription) {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'jump to feature matrix',
        [text]: 'select features',
        [location]: pricingPage,
        [section]: 'A La Carte subscription',
      });

      if (this.subscription.addOns.length === 0) {
        this.updateItem(subscription);
        this.toggleAddOn(this.cloudDirectoryAndSupport);
      }

      this.$emit('viewFeatureMatrix');
    },

    hasAllRequiredComponents(feature) {
      const component = this.getALaCarteComponentFromFeature(feature);

      if (component) {
        // Case: component requires a specific component
        if (component.requires?.length > 0) {
          return this.nonDefaultAddons
            .some(addOn => component.requires.includes(addOn.productCode));
        }

        // Case: component requires any other component (i.e. Premium Support)
        if (component.requiresAtLeastOneAddOn) {
          const isAnyNormalAddOnSelected = this.nonDefaultAddons
            .filter(addOn => !addOn.requiresAtLeastOneAddOn).length > 0;

          return isAnyNormalAddOnSelected;
        }
      }

      // by default, components have no requirements
      return true;
    },

    individualFeaturePrice(subscription, feature) {
      if (feature.infoPerPackage[subscription.type].included) {
        return feature.infoPerPackage[subscription.type].included;
      }

      const featureInfo = this.isALaCarte(subscription)
        ? this.getALaCarteComponentFromFeature(feature)
        : feature.infoPerPackage[subscription.type];

      if (!featureInfo) return '';

      const price = this.isAnnual ? featureInfo.annualPrice : featureInfo.listPrice;

      let priceText = `$${price} /user/mo`;
      if (featureInfo.addOn && this.showAddOns) {
        priceText = `$${price} add-on*`;
      } else if (featureInfo.optional) {
        priceText = `$${price} (optional)`;
      }

      return priceText;
    },

    isALaCarte(subscription) {
      return subscription.productCode === getProductCodeByType(SubscriptionTypes.alaCarte);
    },

    isALaCarteCheckbox(subscription, feature) {
      return this.isALaCarte(subscription)
        && !feature.infoPerPackage[subscription.type].included;
    },

    isCheckboxActive(feature) {
      const productCode = this.getFeatureProductCode(feature);

      return this.addOns.includes(productCode)
        || this.isDefaultAddOn(getProductCodeByType(SubscriptionTypes.alaCarte), productCode);
    },

    isCheckboxDisabled(subscriptionProductCode, feature) {
      const featureProductCode = this.getFeatureProductCode(feature);
      return this.isDefaultAddOn(subscriptionProductCode, featureProductCode)
        || !this.hasAllRequiredComponents(feature);
    },

    isCheckmarked(subscriptionFeatures, featureName) {
      return subscriptionFeatures.some(feature => feature.name === featureName);
    },

    isFeatureShown(feature) {
      return feature.featureFlag
        ? this.$hasFeatureFlag(feature.featureFlag) : true;
    },

    async openPaymentForm(subscription) {
      await this.updateItem(subscription);
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'buy now button clicked',
        [text]: 'buy now',
        [location]: pricingPage,
        [section]: `Feature Pricing Matrix: ${subscription.displayName}: ${this.billingType}`,
      });
      this.$options.PaymentHelper.openCheckoutModal(pricingPage);
    },

    price(subscription) {
      let price = this.subscriptionPrice(subscription.productCode);
      if (!price) {
        price = this.isAnnual ? subscription.annualPrice : subscription.listPrice;
      }
      return price;
    },

    shouldShowFeaturePricing(subscription, feature) {
      if (!feature.infoPerPackage) {
        return false;
      }
      return feature.infoPerPackage[subscription.type];
    },
  },
};
</script>

<style module>
.annualPricing {
  padding-top: var(--jc-spacer-large);
}

.attribute {
  align-items: center;
  display: flex;
  font-weight: 300;
  padding: 1rem 0 1rem 0.5rem;
  width: 20rem;
}

.attributeWithIcon {
  /* overwrite .attribute's padding to give icon space */
  padding: 0.5rem 0 0.5rem 0.5rem;
}

.attributeDetailsContainer {
  display: flex;
  justify-content: center;
}

.attributeCheckbox {
  margin-right: var(--jc-spacer);
}

.attributeRowDescription {
  background-color: var(--jcWhite300);
  height: 40px;
}

.attributeWithDescription {
  text-decoration: underline;
}

.bold {
  font-weight: 600;
}

.cellContent {
  padding: 0 var(--jc-spacer-small);
  text-align: center;
}

.footerButtonContainer {
  margin-top: 1rem;
}

.highlightCellTop {
  border-left: var(--jc-color-informational-base) solid 2px;
  border-right: var(--jc-color-informational-base) solid 2px;
  border-top: var(--jc-color-informational-base) solid 2px;
}

.highlightCellBot {
  border-bottom: var(--jc-color-informational-base) solid 2px;
  border-left: var(--jc-color-informational-base) solid 2px;
  border-right: var(--jc-color-informational-base) solid 2px;
}

.highlightCellSides {
  border-left: var(--jc-color-informational-base) solid 2px;
  border-right: var(--jc-color-informational-base) solid 2px;
}

.featureIcon {
  color: var(--jcPrimary);
}

.featureGroupName {
  font-size: 1.2rem;
  padding: 1.2rem 0 0.4rem;
}

.footerSubscriptionPrice {
  font-size: var(--jc-spacer-large);
  line-height: 2;
}

.subDescription {
  color: var(--jcGrey700);
  font-size: var(--jc-size-0p625);
  font-weight: 300;
  margin-top: var(--jc-spacer-small);
}

.subscriptionHeader {
  padding: var(--jc-spacer) var(--jc-spacer) 0;
  text-align: center;
}

.subscriptionFooter {
  padding: 0 var(--jc-spacer) var(--jc-spacer);
  text-align: center;
}
</style>
